// src/modules/myvault/components/automations/ConditionBuilder.tsx

// What? Simple AND-tree condition builder for automation rules. v1 supports
//       a flat list joined by AND; OR/nested groups are v2.
// Why?  The backend's `_evaluate_conditions` walks AND/OR trees, so we
//       serialise to `{op: "AND", operands: [...leaves...]}` when any
//       conditions exist, and `null` when there are none (= always pass).
// How?  Each row is a leaf `{field, operator, value}`. Field select is
//       populated from the triggering table's columns. Operator + value
//       switch to match what the engine understands; `is_empty` /
//       `is_not_empty` hide the value input.

import React from "react";
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { SchemaColumnResponse } from "../../types";
import type {
  AutomationCondition,
  AutomationConditionGroup,
  AutomationConditionLeaf,
} from "../../hooks/useAutomationRules";

const OPERATORS: {
  value: AutomationConditionLeaf["operator"];
  label: string;
  needsValue: boolean;
}[] = [
  { value: "eq", label: "equals", needsValue: true },
  { value: "neq", label: "not equals", needsValue: true },
  { value: "gt", label: "greater than", needsValue: true },
  { value: "lt", label: "less than", needsValue: true },
  { value: "gte", label: "greater or equal", needsValue: true },
  { value: "lte", label: "less or equal", needsValue: true },
  { value: "contains", label: "contains", needsValue: true },
  { value: "in", label: "in (csv)", needsValue: true },
  { value: "is_empty", label: "is empty", needsValue: false },
  { value: "is_not_empty", label: "is not empty", needsValue: false },
  { value: "changed", label: "changed", needsValue: false },
  { value: "changed_to", label: "changed to", needsValue: true },
  { value: "changed_from", label: "changed from", needsValue: true },
];

function flattenAnd(
  tree: AutomationCondition | null,
): AutomationConditionLeaf[] {
  if (!tree) return [];
  if ("op" in tree && tree.op === "AND") {
    return (tree.operands as AutomationConditionLeaf[]).filter(
      (n) => "field" in n,
    );
  }
  if ("field" in tree) return [tree];
  return [];
}

interface ConditionBuilderProps {
  value: AutomationCondition | null;
  onChange: (next: AutomationCondition | null) => void;
  columns: SchemaColumnResponse[];
}

const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  value,
  onChange,
  columns,
}) => {
  const leaves = flattenAnd(value);

  const serialise = (next: AutomationConditionLeaf[]) => {
    if (next.length === 0) {
      onChange(null);
    } else if (next.length === 1) {
      onChange(next[0]);
    } else {
      const group: AutomationConditionGroup = { op: "AND", operands: next };
      onChange(group);
    }
  };

  const updateLeaf = (
    index: number,
    patch: Partial<AutomationConditionLeaf>,
  ) => {
    const next = leaves.map((leaf, i) =>
      i === index ? { ...leaf, ...patch } : leaf,
    );
    serialise(next);
  };

  const removeLeaf = (index: number) => {
    serialise(leaves.filter((_, i) => i !== index));
  };

  const addLeaf = () => {
    const firstCol = columns[0];
    const next: AutomationConditionLeaf = {
      field: firstCol?.column_name || "",
      operator: "eq",
      value: "",
    };
    serialise([...leaves, next]);
  };

  return (
    <Stack spacing={1}>
      <FormLabel>
        Conditions (all must match — OR groups are a v2 improvement)
      </FormLabel>
      {leaves.length === 0 ? (
        <Sheet
          variant="soft"
          sx={{ borderRadius: "sm", p: 2, textAlign: "center" }}
        >
          <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 1 }}>
            No conditions — rule fires on every matching trigger.
          </Typography>
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            startDecorator={<AddIcon />}
            onClick={addLeaf}
            disabled={columns.length === 0}
          >
            Add condition
          </Button>
        </Sheet>
      ) : (
        <Stack spacing={1}>
          {leaves.map((leaf, index) => {
            const op = OPERATORS.find((o) => o.value === leaf.operator);
            return (
              <Stack
                key={index}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Select
                  size="sm"
                  value={leaf.field}
                  onChange={(_, val) =>
                    updateLeaf(index, { field: (val as string) || "" })
                  }
                  placeholder="field"
                  sx={{ minWidth: 180 }}
                >
                  {columns.map((c) => (
                    <Option key={c.id} value={c.column_name}>
                      {c.display_name || c.column_name}
                    </Option>
                  ))}
                </Select>
                <Select
                  size="sm"
                  value={leaf.operator}
                  onChange={(_, val) =>
                    updateLeaf(index, {
                      operator:
                        (val as AutomationConditionLeaf["operator"]) || "eq",
                    })
                  }
                  sx={{ minWidth: 160 }}
                >
                  {OPERATORS.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
                {op?.needsValue && (
                  <Input
                    size="sm"
                    value={
                      leaf.value === undefined || leaf.value === null
                        ? ""
                        : String(leaf.value)
                    }
                    onChange={(e) =>
                      updateLeaf(index, { value: e.target.value })
                    }
                    placeholder="value"
                    sx={{ flex: 1 }}
                  />
                )}
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => removeLeaf(index)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
          <Box>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              startDecorator={<AddIcon />}
              onClick={addLeaf}
              disabled={columns.length === 0}
            >
              Add condition
            </Button>
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

export default ConditionBuilder;
